import useReduxState from '@portal/hooks/useReduxState';
import { translate } from '@portal/services/i18n';
import React, { ReactNode } from 'react';
import './AdvancedSteps.less';

type StepType = {
  value: string | number;
  label: string;
  children: ReactNode;
};

interface StepOptionsProps {
  steps: StepType[];
  currentStep: string | number;
  onStepChange: (step: string | number) => void;
  calculateProgress?: (currentStep: string | number) => number;
  testId: string;
}

const AdvancedSteps = ({
  steps,
  currentStep,
  onStepChange,
  calculateProgress,
}: StepOptionsProps) => {
  const {
    theme: { currentTheme },
  } = useReduxState();

  const defaultProgress = (step: string | number): number => {
    const index = steps.findIndex((s) => s.value === step);
    if (index === -1) return 0;
    return ((index + 1) / steps.length) * 100;
  };

  const progress = calculateProgress
    ? calculateProgress(currentStep)
    : defaultProgress(currentStep);

  return (
    <div
      className="step-card"
      data-testid="advanced-steps"
      role="group"
      aria-label={translate('COMPONENTS.ADVANCED_STEPS.STEPS_LABEL')}
    >
      <div className="step-card__steps">
        <div className="step-card__steps__line">
          <div
            className="step-card__steps__line__active"
            style={{
              width: `${progress}%`,
              background: currentTheme.background,
            }}
          />
        </div>
        {steps.map((step, index) => {
          const isActive = step.value === currentStep;
          const key = `${String(step.value)}-${index}`;

          return (
            <button
              type="button"
              className="step-card__steps__item transparent-button"
              key={key}
              onClick={() => onStepChange(step.value)}
              disabled
              aria-label={`${translate('COMPONENTS.ADVANCED_STEPS.STEP')} ${
                step.value
              }, ${step.label}`}
              aria-current={isActive ? 'step' : undefined}
            >
              <div
                className="step-card__steps__item__circle"
                style={{
                  background: isActive ? currentTheme.background : '#f9f9f9',
                  color: isActive ? '#fff' : '#000',
                }}
              >
                {step.value}
              </div>
              <div
                className={`step-card__steps__item__label ${
                  step.value === currentStep ? 'active' : ''
                }`}
              >
                {step.label}
              </div>
            </button>
          );
        })}
      </div>
      <div className="step-card__children">
        {steps.find((key) => key.value === currentStep)?.children}
      </div>
    </div>
  );
};

export default AdvancedSteps;
